import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { Component, ComponentType, Viewport, RefreshBadgeConfig } from "@/types";
import type { ColorScheme } from "@/types/color-scheme";
import { cn } from "@/lib/utils";
import { getBreakpointFromViewport } from "@/utils/breakpoint-utils";
import {
  getRenderableResponsiveConstraints,
  hasV2ComponentStyleSlots,
  withNormalizedComponentStyle,
} from "@/utils/component-style-normalization";
import { normalizeLegacySemanticStyleColor } from "@/utils/theme-aware-legacy-colors";
import type { ResponsiveBreakpoint } from "@/stores/editor/responsive-store";
import { useInteractionContext } from "@/components/shared/rendering/InteractionContextRuntime";
import { ComponentWrapper } from "@/components/shared/rendering/ComponentWrapper";
import { useFlexContainerSpatial } from "@/hooks/editor/useFlexContainerSpatial";
import { useContainerChildSelection } from "@/hooks/editor/useContainerChildSelection";
import { useChildManagement } from "@/hooks/editor/useChildManagement";
import { useResponsiveViewport } from "@/hooks/useResponsiveViewport";
import { useContentSlot, useContentSlotActions } from "@/stores/navigation";
import { useRefreshBadge } from "@/hooks/useRefreshBadge";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

interface FlexContainerProps {
  component: Component;
  isPreview?: boolean;
  children?: Component[];
  /** @deprecated Spatial indexing has been removed - kept for backward compatibility */
  spatialSystem?: unknown;
  /** Container position on canvas (used for z-index only) */
  position?: { x: number; y: number; z: number };
  /** Container size */
  size?: { width: number; height: number };
  /** @deprecated Spatial tracking has been removed */
  enableSpatialTracking?: boolean;
  /** @deprecated Collision detection has been removed */
  enableCollisionDetection?: boolean;
  /** Current viewport for responsive behavior (can be Viewport enum or ResponsiveBreakpoint object) */
  viewport?: Viewport | ResponsiveBreakpoint;
  activeColorScheme?: ColorScheme;
}

export interface FlexContainerProperties {
  // Flex Direction
  flexDirection: "row" | "column" | "row-reverse" | "column-reverse";

  // Justify Content (main axis)
  justifyContent:
    | "flex-start"
    | "center"
    | "flex-end"
    | "space-between"
    | "space-around"
    | "space-evenly";

  // Align Items (cross axis)
  alignItems: "stretch" | "flex-start" | "center" | "flex-end" | "baseline";

  // Flex Wrap
  flexWrap: "nowrap" | "wrap" | "wrap-reverse";

  // Gap between items (fallback for both row and column)
  gap?: number | string;

  // Row gap (vertical spacing)
  rowGap?: number | string;

  // Column gap (horizontal spacing)
  columnGap?: number | string;

  // Padding (optional - no hidden default)
  padding?: number | string;

  // Margin
  margin?: number | string;

  // Background
  backgroundColor: string;

  // Border (optional - no hidden default)
  borderRadius?: number;
  border?: string;

  // Box Shadow
  boxShadow?: string;

  // Minimum height for the container (optional - no hidden default)
  minHeight?: number | string;

  // Height may be set via breakpoint style overrides or AI commands.
  // Optional; the container uses 'auto' or '100%' as the default.
  height?: number | string;

  // Container label for identification
  label?: string;
}

/**
 * Detects grid patterns from flexBasis and returns gap-aware flex config.
 * Ported from mobile/src/components/ViewerOnlyCanvas.tsx to ensure cross-platform consistency.
 *
 * Problem: When a container has gap (e.g., 16px) and children have flexBasis: 50%,
 * the total width becomes 50% + 50% + 16px gap = 116%, causing overflow and wrapping issues.
 *
 * Solution: Reduce flexBasis slightly (50% → 45%) and use flexGrow: 1 to fill remaining space.
 * This allows 2 items to fit per row: 45% + 45% + 16px gap ≈ 100%
 */
function getFlexConfig(
  flexBasis: string | number | undefined,
  _containerGap: number = 16
): { flexBasis: string; flexGrow: number; flexShrink: number } | null {
  if (!flexBasis) return null;

  const basisStr = String(flexBasis);

  // 2-column grid (50% basis) → 45% with flexGrow for gap compensation
  if (basisStr.includes('50%') || basisStr.includes('calc(50%')) {
    return { flexBasis: '45%', flexGrow: 1, flexShrink: 0 };
  }

  // 3-column grid (33% basis) → 30% with flexGrow
  if (basisStr.includes('33%') || basisStr.includes('calc(33%')) {
    return { flexBasis: '30%', flexGrow: 1, flexShrink: 0 };
  }

  // 4-column grid (25% basis) → 22% with flexGrow (aligned with mobile app)
  if (basisStr.includes('25%') || basisStr.includes('calc(25%')) {
    return { flexBasis: '22%', flexGrow: 1, flexShrink: 0 };
  }

  return null;
}

function isContentDrivenHeight(value: unknown): boolean {
  if (typeof value !== 'string') return false;
  const normalized = value.trim().toLowerCase();
  return normalized === 'auto'
    || normalized.startsWith('fit-content')
    || normalized.startsWith('max-content')
    || normalized.startsWith('min-content');
}

function hasLegacyDefaultTableMaxHeight(component: Component): boolean {
  if (component.type !== ComponentType.TABLE && component.type !== ComponentType.ADVANCED_TABLE) {
    return false;
  }

  const properties = component.properties as Record<string, unknown> | undefined;
  return properties?.maxHeight === 400
    && properties?.enablePagination !== false
    && properties?.dashboardMode !== true;
}

function hasLegacyDefaultTableFixedHeight(component: Component): boolean {
  if (component.type !== ComponentType.TABLE && component.type !== ComponentType.ADVANCED_TABLE) {
    return false;
  }

  const properties = component.properties as Record<string, unknown> | undefined;
  const style = component.style as React.CSSProperties | undefined;

  return properties?.enablePagination !== false
    && properties?.dashboardMode !== true
    && component.size?.height === 400
    && (style?.height === undefined || style.height === 400 || style.height === '400px')
    && (properties?.height === undefined || properties.height === 400 || properties.height === '400px');
}

function componentUsesContentDrivenHeight(component: Component): boolean {
  const style = component.style as React.CSSProperties | undefined;
  const properties = component.properties as Record<string, unknown> | undefined;
  const nestedStyle = properties?.style as Record<string, unknown> | undefined;
  const isAutoHeightTable =
    (component.type === ComponentType.TABLE || component.type === ComponentType.ADVANCED_TABLE) &&
    (properties?.maxHeight === undefined || hasLegacyDefaultTableMaxHeight(component)) &&
    properties?.enablePagination !== false;

  return isContentDrivenHeight(style?.height)
    || isContentDrivenHeight(style?.minHeight)
    || isContentDrivenHeight(properties?.height)
    || isContentDrivenHeight(properties?.minHeight)
    || isContentDrivenHeight(nestedStyle?.height)
    || isContentDrivenHeight(nestedStyle?.minHeight)
    || hasLegacyDefaultTableFixedHeight(component)
    || isAutoHeightTable;
}

function parsePixelDimension(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string') return 0;
  const match = value.trim().match(/^(\d+(?:\.\d+)?)(px)?$/i);
  return match ? Number(match[1]) : 0;
}

function isToolbarLikeContainer(component: Component): boolean {
  const semantic = `${component.id || ''} ${component.name || ''}`.toLowerCase();
  return /trigger|top controls|top-control|top bar|topbar|nav|footer|breadcrumb|banner/.test(semantic);
}

function shouldIgnoreSmallFixedHeightOverride(component: Component): boolean {
  if (component.type !== ComponentType.FLEX_CONTAINER && component.type !== ComponentType.CONTAINER && (component.type as string) !== 'flex-container') {
    return false;
  }
  if (!component.parentId || isToolbarLikeContainer(component)) return false;

  const properties = component.properties as Record<string, unknown> | undefined;
  if ((properties?.modal as Record<string, unknown> | undefined)?.enabled) return false;

  const style = component.style as React.CSSProperties | undefined;
  const styleHeight = parsePixelDimension(style?.height ?? properties?.height);
  const sizeHeight = parsePixelDimension(component.size?.height);

  return styleHeight > 0 && styleHeight <= 80 && sizeHeight > styleHeight + 24;
}


// ─── Type-safe style extraction helpers ───────────────────────────────────────

/**
 * Narrows a Record<string, unknown> style bag to React.CSSProperties.
 * Only copies keys whose values pass a basic assignability check.
 * Does NOT handle height or minHeight — use narrowDimension() for those.
 */
function extractCSSProperties(bag: Record<string, unknown> | undefined): React.CSSProperties {
  if (bag == null) return {};
  const result: React.CSSProperties = {};

  // String-valued CSS properties — valid when value is a non-empty string
  const stringProps = [
    'background', 'backgroundColor', 'border', 'borderTop', 'borderRight',
    'borderBottom', 'borderLeft', 'borderColor', 'borderStyle', 'boxShadow',
    'scrollBehavior', 'scrollSnapType', 'scrollbarWidth', 'msOverflowStyle',
    'WebkitOverflowScrolling', 'overflow', 'overflowX', 'overflowY',
  ] as const;

  for (const key of stringProps) {
    const value = bag[key];
    if (typeof value === 'string' && value.length > 0) {
      (result as Record<string, string>)[key] = value;
    }
  }

  // Number-or-string CSS properties (e.g., borderRadius can be '8px' or 8)
  const numOrStringProps = [
    'borderRadius', 'borderTopLeftRadius', 'borderTopRightRadius',
    'borderBottomLeftRadius', 'borderBottomRightRadius',
    'borderWidth', 'opacity',
  ] as const;

  for (const key of numOrStringProps) {
    const value = bag[key];
    if (typeof value === 'string' || typeof value === 'number') {
      (result as Record<string, string | number>)[key] = value;
    }
  }

  return result;
}

/**
 * Narrows an unknown value to a number | string dimension, or undefined.
 * Handles pixel values (numbers) and string values (e.g. '50%', '100px').
 */
function narrowDimension(value: unknown): number | string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.length > 0) return value;
  return undefined;
}

function formatDimensionForStyle(value: unknown): string | undefined {
  const dimension = narrowDimension(value);
  if (dimension === undefined) return undefined;
  return typeof dimension === 'number' ? `${dimension}px` : dimension;
}

/**
 * Extracts scroll and overflow-related CSS properties from a component
 * property bag. Returns only values that are valid CSS keyword strings.
 */
function extractScrollProperties(properties: Record<string, unknown>): {
  overflowX: React.CSSProperties['overflowX'];
  overflowY: React.CSSProperties['overflowY'];
  overflow: React.CSSProperties['overflow'];
  flexShrink: number | undefined;
  scrollBehavior: React.CSSProperties['scrollBehavior'];
  scrollSnapType: React.CSSProperties['scrollSnapType'];
  scrollbarWidth: React.CSSProperties['scrollbarWidth'];
  msOverflowStyle: React.CSSProperties['msOverflowStyle'];
  WebkitOverflowScrolling: React.CSSProperties['WebkitOverflowScrolling'];
} {
  const {
    overflowX, overflowY, overflow, flexShrink, scrollBehavior,
    scrollSnapType, scrollbarWidth, msOverflowStyle, WebkitOverflowScrolling,
  } = properties;

  const validOverflow = ['visible', 'hidden', 'clip', 'scroll', 'auto'] as const;
  type OverflowValue = (typeof validOverflow)[number];

  function narrowOverflow(v: unknown): OverflowValue | undefined {
    return (validOverflow as readonly string[]).includes(v as string)
      ? (v as OverflowValue)
      : undefined;
  }

  const validScrollBehavior = ['auto', 'smooth', 'inherit', 'initial', 'revert', 'unset'] as const;
  type ScrollBehaviorValue = React.CSSProperties['scrollBehavior'];

  function narrowScrollBehavior(v: unknown): ScrollBehaviorValue | undefined {
    return (validScrollBehavior as readonly string[]).includes(v as string)
      ? (v as ScrollBehaviorValue)
      : undefined;
  }

  return {
    overflowX: narrowOverflow(overflowX),
    overflowY: narrowOverflow(overflowY),
    overflow: narrowOverflow(overflow),
    flexShrink: typeof flexShrink === 'number' ? flexShrink : undefined,
    scrollBehavior: narrowScrollBehavior(scrollBehavior),
    scrollSnapType: typeof scrollSnapType === 'string' ? scrollSnapType as React.CSSProperties['scrollSnapType'] : undefined,
    scrollbarWidth: typeof scrollbarWidth === 'string' ? scrollbarWidth as React.CSSProperties['scrollbarWidth'] : undefined,
    msOverflowStyle: typeof msOverflowStyle === 'string' ? msOverflowStyle as React.CSSProperties['msOverflowStyle'] : undefined,
    WebkitOverflowScrolling: typeof WebkitOverflowScrolling === 'string' ? WebkitOverflowScrolling as React.CSSProperties['WebkitOverflowScrolling'] : undefined,
  };
}

/** Valid CSS display keywords used to narrow unknown breakpoint values. */
const CSS_DISPLAY_VALUES = [
  'block', 'inline', 'inline-block', 'flex', 'inline-flex',
  'grid', 'inline-grid', 'none', 'contents', 'flow-root',
] as const;

type CSSDisplay = React.CSSProperties['display'];

/**
 * Narrows an unknown value to a valid React.CSSProperties['display'] keyword.
 * Returns undefined for unrecognised values rather than crashing.
 */
function narrowDisplay(value: unknown): CSSDisplay | undefined {
  return (CSS_DISPLAY_VALUES as readonly string[]).includes(value as string)
    ? (value as CSSDisplay)
    : undefined;
}

export const FlexContainer: React.FC<FlexContainerProps> = ({
  component,
  isPreview = false,
  children = [],
  spatialSystem: _spatialSystem, // Deprecated, ignored
  position = { x: 0, y: 0, z: 0 },
  size = { width: 300, height: 200 },
  enableSpatialTracking: _enableSpatialTracking = true, // Deprecated, ignored
  enableCollisionDetection: _enableCollisionDetection = true, // Deprecated, ignored
  viewport = Viewport.DESKTOP,
  activeColorScheme,
}) => {
  // Get interaction context to determine if we're in viewer mode
  const interactionContext = useInteractionContext();

  // Auto-detect responsive breakpoint based on window size
  // PRIORITY: Use viewport prop if provided (from ResponsiveEditorCanvas), fall back to detected breakpoint
  // This ensures editor mobile preview uses the selected viewport, not the actual window size
  // Note: viewport may be a ResponsiveBreakpoint object with width property
  const detectedBreakpoint = useResponsiveViewport();
  const activeViewport = viewport || (detectedBreakpoint as Viewport);
  const activeBreakpointName = getBreakpointFromViewport(activeViewport);
  const renderComponent = useMemo(
    () => withNormalizedComponentStyle(component, activeBreakpointName, activeColorScheme),
    [component, activeBreakpointName, activeColorScheme],
  );

  // Container DOM reference for precise bounds calculation
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Content slot subscription for content swapping (Phase 4)
  const contentSlot = useContentSlot(renderComponent.id);
  const { registerContentSlot, unregisterContentSlot } = useContentSlotActions();

  // Register content slot if component has contentSlotChildren property
  useEffect(() => {
    const contentSlotChildren = renderComponent.properties?.contentSlotChildren as string[] | undefined;

    if (contentSlotChildren && contentSlotChildren.length > 0) {
      registerContentSlot(renderComponent.id, contentSlotChildren);

      return () => {
        unregisterContentSlot(renderComponent.id);
      };
    }
    return undefined;
  }, [renderComponent.id, renderComponent.properties?.contentSlotChildren, registerContentSlot, unregisterContentSlot]);

  // Filter children based on content slot state (Phase 4)
  const filteredChildren = useMemo(() => {
    if (!contentSlot) return children;

    const { activeContentId, availableContentIds } = contentSlot;

    return children.filter(child => {
      // Non-slot children always visible
      if (!availableContentIds.includes(child.id)) return true;
      // Slot children: only show active
      return child.id === activeContentId;
    });
  }, [children, contentSlot]);

  // FlexContainer properties - NO hidden defaults for spacing/sizing properties
  // Only flex layout properties have defaults (direction, justify, align, wrap)
  // Phase 6.3: Also read flex CSS properties from component.style (set by AI commands)
  const properties = useMemo<FlexContainerProperties>(() => {
    const normalizedPropertyBackgroundColor =
      activeColorScheme && typeof renderComponent.properties?.backgroundColor === "string"
        ? normalizeLegacySemanticStyleColor(
            "backgroundColor",
            renderComponent.properties.backgroundColor,
            renderComponent,
          )
        : renderComponent.properties?.backgroundColor;
    return ({
    // Spread base properties first, cast to partial to avoid type conflicts
    ...(renderComponent.properties as Partial<FlexContainerProperties>),
    // Flex layout properties - typed defaults override undefined/invalid values from spread
    flexDirection: (renderComponent.properties?.flexDirection as FlexContainerProperties['flexDirection']) || "column",
    justifyContent: (renderComponent.properties?.justifyContent as FlexContainerProperties['justifyContent']) || "flex-start",
    alignItems: (renderComponent.properties?.alignItems as FlexContainerProperties['alignItems']) || "stretch",
    flexWrap: (renderComponent.properties?.flexWrap as FlexContainerProperties['flexWrap']) || "nowrap",
    // Spacing/sizing properties - NO defaults, only use explicit values
    // Use conditional spreads for optional properties to avoid undefined type conflicts
    gap: renderComponent.properties?.gap as FlexContainerProperties['gap'],
    rowGap: renderComponent.properties?.rowGap as FlexContainerProperties['rowGap'],
    columnGap: renderComponent.properties?.columnGap as FlexContainerProperties['columnGap'],
    padding: renderComponent.properties?.padding as FlexContainerProperties['padding'],
    backgroundColor: (normalizedPropertyBackgroundColor as string) || "transparent",
    minHeight: renderComponent.properties?.minHeight as FlexContainerProperties['minHeight'],
    label: renderComponent.properties?.label as FlexContainerProperties['label'],
    // Optional properties - only include if defined to avoid type conflicts
    ...(renderComponent.properties?.borderRadius !== undefined && { borderRadius: renderComponent.properties.borderRadius as number }),
    // Phase 6.3: Override with style values if present (AI commands set these in style)
    // Type assertions needed because React.CSSProperties uses broader string types
    ...(renderComponent.style?.gap !== undefined && { gap: renderComponent.style.gap as number }),
    ...(renderComponent.style?.justifyContent !== undefined && { justifyContent: renderComponent.style.justifyContent as FlexContainerProperties['justifyContent'] }),
    ...(renderComponent.style?.alignItems !== undefined && { alignItems: renderComponent.style.alignItems as FlexContainerProperties['alignItems'] }),
    ...(renderComponent.style?.flexDirection !== undefined && { flexDirection: renderComponent.style.flexDirection as FlexContainerProperties['flexDirection'] }),
    ...(renderComponent.style?.flexWrap !== undefined && { flexWrap: renderComponent.style.flexWrap as FlexContainerProperties['flexWrap'] }),
    // Phase 4: Add padding override from style (AI spacing commands set this)
    // Handle both string ('0px') and number (0) values
    ...(renderComponent.style?.padding !== undefined && {
      padding: typeof renderComponent.style.padding === 'string'
        ? (renderComponent.style.padding === '0px' || renderComponent.style.padding === '0' ? 0 : renderComponent.style.padding)
        : renderComponent.style.padding as number
    }),
  }); }, [renderComponent.properties, renderComponent.style, activeColorScheme]);

  // Phase 6.3: Style overrides are applied via properties object above


  // Apply responsive overrides based on active viewport
  // Now works in editing mode to provide real-time responsive preview
  const responsiveProperties = useMemo(() => {
    const { responsivePosition } = renderComponent;
    const breakpoint = getBreakpointFromViewport(activeViewport);

    // Start with default properties
    let props = { ...properties };

    // Apply breakpoint overrides if they exist
    // Phase 5 Fix: Read from BOTH style AND properties
    // BreakpointPropertyService stores flexDirection/justifyContent/alignItems in properties
    // but stores backgroundColor/padding/gap etc. in style
    if (responsivePosition?.breakpoints?.[breakpoint]) {
      const breakpointOverride = responsivePosition.breakpoints[breakpoint];

      // Apply style overrides (backgroundColor, padding, gap, etc.)
      if (breakpointOverride.style) {
        props = {
          ...props,
          ...(breakpointOverride.style as Partial<FlexContainerProperties>)
        };
      }

      // Phase 5: Apply properties overrides (flexDirection, justifyContent, alignItems)
      // These are set by AI breakpoint commands like "tablet: direction column"
      if (breakpointOverride.properties) {
        const bpProps = breakpointOverride.properties as Partial<FlexContainerProperties>;
        if (bpProps.flexDirection) props.flexDirection = bpProps.flexDirection;
        if (bpProps.justifyContent) props.justifyContent = bpProps.justifyContent;
        if (bpProps.alignItems) props.alignItems = bpProps.alignItems;
        if (bpProps.flexWrap) props.flexWrap = bpProps.flexWrap;
      }
    }

    // Phase 4: Apply component.style.padding as fallback
    // Phase 5 Fix: Only apply base padding if NO breakpoint-specific padding exists
    // Breakpoint-specific padding (from "on mobile padding 50") takes priority
    const hasBreakpointPadding = responsivePosition?.breakpoints?.[breakpoint]?.style?.padding !== undefined;
    if (renderComponent.style?.padding !== undefined && !hasBreakpointPadding) {
      props.padding = typeof renderComponent.style.padding === 'string'
        ? (renderComponent.style.padding === '0px' || renderComponent.style.padding === '0' ? 0 : renderComponent.style.padding)
        : renderComponent.style.padding as number;
    }

    // For mobile/tablet breakpoints with row direction, enable flexWrap for grid layouts
    // This ensures 2x2 grids work properly by allowing children to wrap to next row
    // Matches mobile app behavior from ViewerOnlyCanvas.tsx
    if ((breakpoint === 'mobile' || breakpoint === 'tablet') &&
        (props.flexDirection === 'row' || props.flexDirection === 'row-reverse')) {
      // Check if any child has a grid-pattern flexBasis (50%, 33%, 25%)
      const hasGridChildren = children.some(child => {
        const childConstraints = child.responsivePosition?.breakpoints?.[breakpoint]?.constraints;
        const normalizedConstraints = getRenderableResponsiveConstraints(child, breakpoint);
        const childFlexBasis = childConstraints?.flexBasis || normalizedConstraints.flexBasis;
        return (typeof childFlexBasis === 'string' || typeof childFlexBasis === 'number') &&
          getFlexConfig(childFlexBasis) !== null;
      });

      // Enable wrap if there are grid children (auto-enable for grid layouts)
      // The default flexWrap is 'nowrap', so we override it when children need grid layout
      if (hasGridChildren) {
        props.flexWrap = 'wrap';
      }
    }

    return props;
  }, [renderComponent, properties, activeViewport, children]);

  // Refresh badge: detect config and use hook
  const refreshBadgeConfig = renderComponent.properties?.refreshBadge as RefreshBadgeConfig | undefined;
  const isRefreshBadge = refreshBadgeConfig?.enabled === true;
  const { triggerRefresh, isRefreshing } = useRefreshBadge({
    scopeContainerId: isRefreshBadge ? refreshBadgeConfig.scopeContainerId : undefined,
    enabled: isRefreshBadge,
  });
  const handleRefreshClick = useCallback((e: React.MouseEvent) => {
    if (!isRefreshBadge) return;
    e.stopPropagation();
    triggerRefresh();
  }, [isRefreshBadge, triggerRefresh]);
  const handleRefreshKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isRefreshBadge) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      triggerRefresh();
    }
  }, [isRefreshBadge, triggerRefresh]);

  // Child selection functionality
  const {
    selectionState,
    isChildSelected,
    isChildFocused,
    createChildKeydownHandler,
    hasChildSelection
  } = useContainerChildSelection({
    containerId: renderComponent.id,
    enableMultiSelect: true,
    autoFocusFirstChild: false,
    onChildSelect: () => {
      // Child selection callback
    },
    // onSelectionChange removed to prevent infinite loop
  });

  // Child management with confirmations
  const {
    confirmationState,
    removeChildWithConfirmation,
    removeChildrenWithConfirmation,
    duplicateChild,
    duplicateChildren,
    confirmOperation,
    cancelOperation
  } = useChildManagement({
    containerId: renderComponent.id,
    requireConfirmation: !isPreview,
    enableUndo: true,
    onChildRemove: () => {
      // Child remove callback
    },
    onChildDuplicate: () => {
      // Child duplicate callback
    },
    onBulkOperation: () => {
      // Bulk operation callback
    }
  });

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    show: boolean;
    x: number;
    y: number;
    childId: string | null;
    isMultiSelect: boolean;
  }>({ show: false, x: 0, y: 0, childId: null, isMultiSelect: false });

  const { style } = renderComponent;

  // Convert properties for the spatial hook (which doesn't need margin/boxShadow)
  // Note: gap/padding default to 0 if not specified (no hidden defaults)
  // Spatial hook requires concrete values, so provide defaults for optional properties
  const spatialProperties = {
    flexDirection: properties.flexDirection,
    justifyContent: properties.justifyContent,
    alignItems: properties.alignItems,
    flexWrap: properties.flexWrap,
    gap: typeof properties.gap === 'number' ? properties.gap : (typeof properties.gap === 'string' ? parseFloat(properties.gap) || 0 : 0),
    padding: typeof properties.padding === 'number' ? properties.padding : (typeof properties.padding === 'string' ? parseFloat(properties.padding) || 0 : 0),
    backgroundColor: properties.backgroundColor,
    borderRadius: properties.borderRadius ?? 0,
    border: properties.border,
    minHeight: typeof properties.minHeight === 'number' ? properties.minHeight : 0
  };

  // Spatial hook (now returns stub values - kept for backward compatibility)
  const flexContainerSpatial = useFlexContainerSpatial(
    {
      component,
      position,
      size,
      properties: spatialProperties,
      children,
      containerRef,
      trackChildren: false, // Spatial tracking deprecated
      enableCollisions: false, // Collision detection deprecated
    }
  );

  // Convert Tailwind-like values to CSS values
  const getJustifyContentValue = (value: string) => {
    switch (value) {
      case "start":
        return "flex-start";
      case "end":
        return "flex-end";
      case "between":
        return "space-between";
      case "around":
        return "space-around";
      case "evenly":
        return "space-evenly";
      default:
        return value;
    }
  };

  const getAlignItemsValue = (value: string) => {
    switch (value) {
      case "start":
        return "flex-start";
      case "end":
        return "flex-end";
      default:
        return value;
    }
  };

  // For nested containers, don't apply their own border/shadow - parent handles that
  const isNestedContainer = renderComponent.parentId && renderComponent.parentId !== 'root';

  // CRITICAL FIX: Do NOT spread component.style into containerStyle
  // component.style (padding, margin, background, borderRadius, etc.) belongs ONLY on ComponentWrapper
  // Spreading it here creates duplicate styling where both wrapper and FlexContainer have the same
  // styles, causing the wrapper to extend past the FlexContainer content.
  // The FlexContainer should only use its internal flex properties and responsiveProperties.
  // See ai-editor-system.md Section IV.12 for documentation.
  const shouldIgnoreFixedHeight = shouldIgnoreSmallFixedHeightOverride(renderComponent);
  const hasContentDrivenHeight = componentUsesContentDrivenHeight(renderComponent) || shouldIgnoreFixedHeight;
  const scrollProps = extractScrollProperties(renderComponent.properties);
  const typedStyle = extractCSSProperties(style);

  const resolvedContainerHeight = shouldIgnoreFixedHeight
    ? undefined
    : (responsiveProperties.height || narrowDimension(style?.['height']) || narrowDimension(renderComponent.properties['height']));
  const containerHeight = hasContentDrivenHeight
    ? (resolvedContainerHeight || 'auto')
    : (resolvedContainerHeight || '100%');

  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: responsiveProperties.flexDirection,
    justifyContent: getJustifyContentValue(responsiveProperties.justifyContent),
    alignItems: getAlignItemsValue(responsiveProperties.alignItems),
    flexWrap: responsiveProperties.flexWrap,
    // Use rowGap and columnGap if specified, otherwise fall back to gap
    // Handle both number and string values (breakpoints may pass strings like "16px")
    rowGap: responsiveProperties.rowGap !== undefined
      ? (typeof responsiveProperties.rowGap === 'number' ? `${responsiveProperties.rowGap}px` : responsiveProperties.rowGap)
      : responsiveProperties.gap !== undefined
        ? (typeof responsiveProperties.gap === 'number' ? `${responsiveProperties.gap}px` : responsiveProperties.gap)
        : undefined,
    columnGap: responsiveProperties.columnGap !== undefined
      ? (typeof responsiveProperties.columnGap === 'number' ? `${responsiveProperties.columnGap}px` : responsiveProperties.columnGap)
      : responsiveProperties.gap !== undefined
        ? (typeof responsiveProperties.gap === 'number' ? `${responsiveProperties.gap}px` : responsiveProperties.gap)
        : undefined,
    // Remove padding from here - it's now applied to the inner container with children
    // Don't apply margin and boxShadow here - they go on the wrapper div for children
    // Keep the background color for all containers
    // Phase 1: Support gradients via style.background and solid colors via style.backgroundColor
    // IMPORTANT: Don't mix backgroundColor and background - they conflict in React/CSS
    // Priority: style.background (if gradient) > style.backgroundColor > responsiveProperties.backgroundColor
    // NOTE: 'none' is used to clear gradients - treat it as falsy to allow backgroundColor to take effect
    ...(typedStyle.background && typedStyle.background !== 'none'
      ? { background: typedStyle.background }
      : responsiveProperties.backgroundColor === 'transparent'
        ? { backgroundColor: 'transparent' }
        : { backgroundColor: typedStyle.backgroundColor || responsiveProperties.backgroundColor }),
    // Keep border radius for all containers (important for overflow clipping)
    // Phase 2: Support borderRadius from style (AI commands) with priority over properties
    // IMPORTANT: Don't mix shorthand borderRadius with individual corner properties to avoid React warnings
    // Check if any individual corner radius is set - if so, don't set shorthand
    // NO hidden default: only apply borderRadius if explicitly set
    ...(typedStyle.borderTopLeftRadius !== undefined ||
        typedStyle.borderTopRightRadius !== undefined ||
        typedStyle.borderBottomLeftRadius !== undefined ||
        typedStyle.borderBottomRightRadius !== undefined
      ? {} // Individual corner radii will be applied below, don't set shorthand
      : typedStyle.borderRadius !== undefined
        ? { borderRadius: typedStyle.borderRadius }
        : responsiveProperties.borderRadius !== undefined
          ? { borderRadius: `${responsiveProperties.borderRadius}px` }
          : {}),
    // Don't apply minHeight - let the container size based on its children by default
    // Users can manually set minHeight via style property if they want to override this
    // This allows containers to adapt to any child content (text, charts, etc.)
    // minHeight: removed to allow content-based sizing
    // Phase 2: Border handling - support border properties from AI commands
    // Priority: style.border (shorthand) > style.borderWidth/Style/Color > responsiveProperties.border > editing default
    ...(typedStyle.border ? {
      // Use shorthand border property when set by AI commands (e.g., "border 2px solid red")
      border: typedStyle.border,
    } : typedStyle.borderWidth || typedStyle.borderStyle || typedStyle.borderColor ? {
      // Use individual border properties when set by AI commands
      borderWidth: typedStyle.borderWidth,
      borderStyle: typedStyle.borderStyle || 'solid',
      borderColor: typedStyle.borderColor,
    } : {
      // Fall back to shorthand border property from properties or editing default
      // No border for nested containers or in viewer mode
      // Use interactionContext.mode instead of isPreview for more reliable detection
      border: isNestedContainer ?
        "none" :
        (responsiveProperties.border || (isPreview || interactionContext.mode !== 'editing' ? "none" : "2px dashed #e2e8f0")),
    }),
    // Box shadow: only from component.style (AI commands). properties/responsiveProperties boxShadow
    // is handled by the parent's child wrapper div (~lines 1176-1191) which also sets borderRadius + overflow:hidden.
    // Applying it here too causes duplicate shadows with pointed-corner artifacts.
    boxShadow: typedStyle.boxShadow || (isNestedContainer ? "none" : undefined),
    // Opacity: apply from style if specified (used for gradient transparency)
    ...(typedStyle.opacity !== undefined && { opacity: typedStyle.opacity }),
    // Phase 2: Individual border side support (AI commands like "border top 2px solid red")
    ...(typedStyle.borderTop !== undefined && { borderTop: typedStyle.borderTop }),
    ...(typedStyle.borderRight !== undefined && { borderRight: typedStyle.borderRight }),
    ...(typedStyle.borderBottom !== undefined && { borderBottom: typedStyle.borderBottom }),
    ...(typedStyle.borderLeft !== undefined && { borderLeft: typedStyle.borderLeft }),
    // Phase 2: Individual corner border radius support (AI commands like "round top left 20")
    ...(typedStyle.borderTopLeftRadius !== undefined && { borderTopLeftRadius: typedStyle.borderTopLeftRadius }),
    ...(typedStyle.borderTopRightRadius !== undefined && { borderTopRightRadius: typedStyle.borderTopRightRadius }),
    ...(typedStyle.borderBottomLeftRadius !== undefined && { borderBottomLeftRadius: typedStyle.borderBottomLeftRadius }),
    ...(typedStyle.borderBottomRightRadius !== undefined && { borderBottomRightRadius: typedStyle.borderBottomRightRadius }),
    position: "relative",
    // Width for the container - always use 100% as the wrapper div controls sizing
    width: "100%",
    // Height: use explicit height from style/properties if provided, otherwise 100% to respect parent constraints
    // Using 100% instead of auto ensures nested FlexContainers respect parent height limits
    height: containerHeight,
    // MinHeight: use explicit minHeight from responsiveProperties (breakpoint-aware) first, then style/properties
    minHeight: responsiveProperties.minHeight || narrowDimension(style?.['minHeight']) || narrowDimension(renderComponent.properties['minHeight']) || 0,
    minWidth: 0,
    // Apply flexShrink from properties if specified
    ...(scrollProps.flexShrink !== undefined && {
      flexShrink: scrollProps.flexShrink,
    }),
    // Explicitly set box-sizing to ensure borders are included in width
    boxSizing: "border-box",
    // Handle overflow - if overflowX or overflowY is specified, use those instead of generic overflow
    // Default: 'hidden' when container has borderRadius (to clip children to rounded corners),
    // 'visible' otherwise (so children's box-shadows aren't clipped by the parent)
    ...(() => {
      const overflowX = scrollProps.overflowX;
      const overflowY = scrollProps.overflowY;
      const overflow = scrollProps.overflow;
      const hasBorderRadius = typedStyle.borderRadius !== undefined
        || typedStyle.borderTopLeftRadius !== undefined
        || typedStyle.borderTopRightRadius !== undefined
        || typedStyle.borderBottomLeftRadius !== undefined
        || typedStyle.borderBottomRightRadius !== undefined
        || responsiveProperties.borderRadius !== undefined;

      if (overflowX !== undefined || overflowY !== undefined) {
        // Use specific overflow directions
        return {
          overflowX: overflowX || 'visible',
          overflowY: overflowY || 'visible',
        };
      } else {
        // Use generic overflow
        return {
          overflow: overflow || (hasBorderRadius ? 'hidden' : 'visible'),
        };
      }
    })(),
    // Apply scroll behavior for smooth scrolling
    scrollBehavior: scrollProps.scrollBehavior,
    // Apply scroll snap type for carousel/slider functionality
    scrollSnapType: scrollProps.scrollSnapType,
    // Hide scrollbars for carousel effect
    scrollbarWidth: scrollProps.scrollbarWidth,
    msOverflowStyle: scrollProps.msOverflowStyle,
    WebkitOverflowScrolling: scrollProps.WebkitOverflowScrolling,
  };

  // Show different states based on whether container has children
  const isEmpty = React.useMemo(() => {
    const hasNoChildren = !children || children.length === 0;
    return hasNoChildren;
  }, [children]);

  return (
    <div
      ref={(node) => {
        // Multi-ref handling for containerRef
        // Use callback pattern to avoid read-only ref assignment
        containerRef.current = node;
      }}
      className={cn(
        "relative w-full transition-all duration-200",
        renderComponent.parentId && !hasContentDrivenHeight && "h-full",
        // Enable pointer events for containers so they can be selected
        isEmpty && "hover:border-blue-400 hover:bg-blue-50/30",
        isRefreshing && "animate-pulse",
        isRefreshBadge && "cursor-pointer",
      )}
      style={containerStyle}
      onClickCapture={isRefreshBadge ? handleRefreshClick : undefined}
      onKeyDown={isRefreshBadge ? handleRefreshKeyDown : undefined}
      role={isRefreshBadge ? 'button' : undefined}
      tabIndex={isRefreshBadge ? 0 : undefined}
      aria-label={isRefreshBadge ? 'Refresh data' : undefined}
      data-component-type="flex-container"
      data-flex-direction={responsiveProperties.flexDirection}
      data-container-id={renderComponent.id}
      data-nesting-depth={flexContainerSpatial.containerBounds.nestingDepth || 0}
    >
      {/* Refresh badge: force pointer cursor and disable child pointer events.
          ComponentWrapper sets inline pointer-events:auto / cursor:default which
          can only be overridden via !important in a stylesheet. */}
      {isRefreshBadge && (
        <style>{`
          [data-container-id="${renderComponent.id}"] * {
            pointer-events: none !important;
            cursor: pointer !important;
          }
        `}</style>
      )}
      {isEmpty ? (
        // Empty state - render empty container (no placeholder visualization)
        <div className="flex-1 pointer-events-auto" />
      ) : (
        // Render children components - apply flex layout to this inner container
        // Don't apply h-full - let container size based on children
        <div
          className="pointer-events-auto w-full relative"
          style={{
            display: "flex",
            flexDirection: responsiveProperties.flexDirection,
            justifyContent: getJustifyContentValue(responsiveProperties.justifyContent),
            alignItems: getAlignItemsValue(responsiveProperties.alignItems),
            flexWrap: responsiveProperties.flexWrap,
            // Use rowGap and columnGap if specified, otherwise fall back to gap
            // Handle both number and string values (breakpoints may pass strings like "16px")
            rowGap: responsiveProperties.rowGap !== undefined
              ? (typeof responsiveProperties.rowGap === 'number' ? `${responsiveProperties.rowGap}px` : responsiveProperties.rowGap)
              : responsiveProperties.gap !== undefined
                ? (typeof responsiveProperties.gap === 'number' ? `${responsiveProperties.gap}px` : responsiveProperties.gap)
                : undefined,
            columnGap: responsiveProperties.columnGap !== undefined
              ? (typeof responsiveProperties.columnGap === 'number' ? `${responsiveProperties.columnGap}px` : responsiveProperties.columnGap)
              : responsiveProperties.gap !== undefined
                ? (typeof responsiveProperties.gap === 'number' ? `${responsiveProperties.gap}px` : responsiveProperties.gap)
                : undefined,
            // Phase 5 Fix: ALWAYS use individual padding properties to avoid React warnings
            // React doesn't allow switching between shorthand (padding) and individual (paddingTop, etc.)
            // So we always use individual properties and convert shorthand to individual
            // NO hidden defaults: if padding is not specified, no padding is applied
            ...((() => {
              const styleWithPadding = renderComponent.style as React.CSSProperties | undefined;
              const hasIndividualPadding = styleWithPadding?.paddingTop !== undefined ||
                                          styleWithPadding?.paddingRight !== undefined ||
                                          styleWithPadding?.paddingBottom !== undefined ||
                                          styleWithPadding?.paddingLeft !== undefined;

              // Parse CSS shorthand padding (e.g., "20px 24px") into individual values
              // Returns undefined if padding is not specified (no hidden defaults)
              const parsePaddingShorthand = (padding: string | number | undefined): {
                paddingTop: string;
                paddingRight: string;
                paddingBottom: string;
                paddingLeft: string;
              } | undefined => {
                // No padding specified - return undefined (no hidden defaults)
                if (padding === undefined || padding === null) {
                  return undefined;
                }

                if (typeof padding === 'number') {
                  const value = `${padding}px`;
                  return { paddingTop: value, paddingRight: value, paddingBottom: value, paddingLeft: value };
                }

                const values = padding.trim().split(/\s+/);

                switch (values.length) {
                  case 1:
                    return {
                      paddingTop: values[0],
                      paddingRight: values[0],
                      paddingBottom: values[0],
                      paddingLeft: values[0]
                    };
                  case 2:
                    // "20px 24px" → top/bottom: 20px, right/left: 24px
                    return {
                      paddingTop: values[0],
                      paddingRight: values[1],
                      paddingBottom: values[0],
                      paddingLeft: values[1]
                    };
                  case 3:
                    // "10px 20px 15px" → top: 10px, right/left: 20px, bottom: 15px
                    return {
                      paddingTop: values[0],
                      paddingRight: values[1],
                      paddingBottom: values[2],
                      paddingLeft: values[1]
                    };
                  case 4:
                    // "10px 20px 15px 25px" → top: 10px, right: 20px, bottom: 15px, left: 25px
                    return {
                      paddingTop: values[0],
                      paddingRight: values[1],
                      paddingBottom: values[2],
                      paddingLeft: values[3]
                    };
                  default:
                    // Invalid padding format - no padding applied
                    return undefined;
                }
              };

              // Parse the padding from responsiveProperties (handles shorthand like "20px 24px")
              // NO fallback to 16 - if not specified, no padding is applied
              const parsedPadding = parsePaddingShorthand(responsiveProperties.padding);

              if (hasIndividualPadding) {
                // Use individual padding properties, falling back to parsed values for unset sides
                // If no parsed padding, individual sides still get applied
                return {
                  paddingTop: styleWithPadding?.paddingTop ?? parsedPadding?.paddingTop,
                  paddingRight: styleWithPadding?.paddingRight ?? parsedPadding?.paddingRight,
                  paddingBottom: styleWithPadding?.paddingBottom ?? parsedPadding?.paddingBottom,
                  paddingLeft: styleWithPadding?.paddingLeft ?? parsedPadding?.paddingLeft,
                };
              } else if (parsedPadding) {
                // Use parsed padding values (properly handles shorthand)
                return parsedPadding;
              } else {
                // No padding specified - return empty object (no padding styles)
                return {};
              }
            })()),
            // Apply height from containerStyle to inner wrapper as well
            // Default to 100% to propagate parent height constraints through the entire chain
            height: containerHeight,
          }}
        >
          {/* Deduplicate children by ID to prevent React key warnings */}
          {Array.from(new Map(filteredChildren.map(c => [c.id, c])).values()).map((child, index) => {
            const breakpoint = getBreakpointFromViewport(activeViewport);
            const renderChild = withNormalizedComponentStyle(child, breakpoint, activeColorScheme);
            const normalizedChildConstraints = getRenderableResponsiveConstraints(renderChild, breakpoint);
            const isSelected = isChildSelected(child.id);
            const isFocused = isChildFocused(child.id);
            const childKeydownHandler = createChildKeydownHandler(child.id);
            const childStyle = renderChild.style;
            const childUsesV2StyleSlots = hasV2ComponentStyleSlots(child);


            // Compute all flex styles here using separate properties (not shorthand)
            // This avoids browser parsing issues with the flex shorthand
            const computedFlexConfig = (() => {
              // No hidden default for gap - use 0 if not specified
              const containerGap = typeof responsiveProperties.gap === 'number'
                ? responsiveProperties.gap
                : (typeof responsiveProperties.gap === 'string' ? parseFloat(responsiveProperties.gap) || 0 : 0);

              if (renderChild.responsivePosition) {
                const breakpointConstraints = renderChild.responsivePosition.breakpoints?.[breakpoint]?.constraints;

                if (breakpointConstraints?.flexBasis !== undefined) {
                  // Check for grid pattern and apply gap-aware sizing
                  const gridConfig = getFlexConfig(breakpointConstraints.flexBasis, containerGap);
                  if (gridConfig) {
                    // Use gap-aware values for grid layouts (matches mobile app behavior)
                    return gridConfig;
                  }
                  // No grid pattern - use raw values
                  return {
                    flexGrow: breakpointConstraints.flexGrow ?? 0,
                    flexShrink: breakpointConstraints.flexShrink ?? 1,
                    flexBasis: breakpointConstraints.flexBasis,
                  };
                }

                if (breakpointConstraints?.flexGrow !== undefined ||
                    breakpointConstraints?.flexShrink !== undefined) {
                  return {
                    flexGrow: breakpointConstraints.flexGrow ?? 0,
                    flexShrink: breakpointConstraints.flexShrink ?? 1,
                    flexBasis: 'auto',
                  };
                }

                // Fallback to base constraints
                if (normalizedChildConstraints) {
                  const { flexGrow, flexShrink, flexBasis } = normalizedChildConstraints;
                  const normalizedFlexBasis =
                    typeof flexBasis === 'string' || typeof flexBasis === 'number'
                      ? flexBasis
                      : undefined;
                  if (normalizedFlexBasis !== undefined) {
                    // Check for grid pattern in base constraints too
                    const gridConfig = getFlexConfig(normalizedFlexBasis, containerGap);
                    if (gridConfig) {
                      return gridConfig;
                    }
                    return {
                      flexGrow: typeof flexGrow === 'number' ? flexGrow : 0,
                      flexShrink: typeof flexShrink === 'number' ? flexShrink : 1,
                      flexBasis: normalizedFlexBasis,
                    };
                  }
                  if (flexGrow !== undefined || flexShrink !== undefined) {
                    return {
                      flexGrow: typeof flexGrow === 'number' ? flexGrow : 0,
                      flexShrink: typeof flexShrink === 'number' ? flexShrink : 1,
                      flexBasis: 'auto',
                    };
                  }
                }
              }
              // Check if child.properties?.flex is a valid string - parse it
              const flexProp = renderChild.properties?.flex;
              if (flexProp && typeof flexProp === 'string') {
                const parts = flexProp.split(' ');
                if (parts.length >= 3) {
                  return {
                    flexGrow: parseInt(parts[0], 10) || 0,
                    flexShrink: parseInt(parts[1], 10) || 1,
                    flexBasis: parts[2],
                  };
                }
              }
              return { flexGrow: 0, flexShrink: 1, flexBasis: 'auto' };
            })();

            const normalizedChildStyleWidth = narrowDimension(childStyle?.width);
            const normalizedChildStyleHeight = narrowDimension(childStyle?.height);
            const childStyleWidth = normalizedChildStyleWidth === undefined
              ? undefined
              : typeof normalizedChildStyleWidth === 'number'
                ? `${normalizedChildStyleWidth}px`
                : normalizedChildStyleWidth;
            const childStyleHeight = normalizedChildStyleHeight === undefined
              ? undefined
              : typeof normalizedChildStyleHeight === 'number'
                ? `${normalizedChildStyleHeight}px`
                : normalizedChildStyleHeight;
            const hasStyleDrivenWidth =
              renderChild.size?.width === 'auto' &&
              normalizedChildStyleWidth !== undefined &&
              normalizedChildStyleWidth !== 'auto';
            const hasExplicitAutoWidthIntent =
              childStyle?.width === 'auto' ||
              renderChild.properties?.width === 'auto' ||
              renderChild.responsivePosition?.responsiveSize?.width === 'auto';
            // Check if child is an auto-width flex-container with content-sizing constraints
            // These need width: fit-content on wrapper to prevent inner FlexContainer's width: 100% from stretching
            const isAutoWidthFlexContainer = (
              (renderChild.type === ComponentType.FLEX_CONTAINER || renderChild.type === ComponentType.CONTAINER) &&
              renderChild.size?.width === 'auto' &&
              hasExplicitAutoWidthIntent &&
              !hasStyleDrivenWidth &&
              computedFlexConfig.flexGrow === 0 &&
              computedFlexConfig.flexBasis === 'auto'
            );
            const isRowFlexGrowingChild =
              computedFlexConfig.flexGrow > 0 &&
              (responsiveProperties.flexDirection === 'row' ||
                responsiveProperties.flexDirection === 'row-reverse');
            const childHasContentDrivenHeight = componentUsesContentDrivenHeight(renderChild) || shouldIgnoreSmallFixedHeightOverride(renderChild);
            const shouldUseV2StyleWidth =
              childUsesV2StyleSlots &&
              normalizedChildStyleWidth !== undefined &&
              normalizedChildStyleWidth !== 'auto' &&
              !isRowFlexGrowingChild;
            const shouldUseV2StyleHeight =
              childUsesV2StyleSlots &&
              normalizedChildStyleHeight !== undefined &&
              normalizedChildStyleHeight !== 'auto' &&
              !childHasContentDrivenHeight;

            const childStyles: React.CSSProperties = {
              // Apply flex properties individually (not shorthand) for better browser compatibility
              flexGrow: computedFlexConfig.flexGrow,
              flexShrink: computedFlexConfig.flexShrink,
              flexBasis: computedFlexConfig.flexBasis,
              // Allow flex items to shrink below content size (prevents overflow past parent)
              minWidth: 0,
              minHeight: 0,
              // When child has flexGrow in a ROW layout, make wrapper a grid container so the child
              // FlexContainer (with height:100%) fills the wrapper — required for equal-height cards.
              // In COLUMN layouts, skip this so content stays content-sized (prevents value being
              // vertically centered in a large grown area).
              ...(isRowFlexGrowingChild
                ? { display: 'grid' }
                : {}),
              // Apply explicit width/height from component.size (unified sizing model)
              // AI commands write to component.size, so we read from there for flex children
              // For auto-width flex-containers with zero growth, use fit-content to enable content-sizing
              width: isAutoWidthFlexContainer
                ? 'fit-content'
                : (isRowFlexGrowingChild && renderChild.size?.width === 'auto')
                  ? undefined
                : shouldUseV2StyleWidth
                  ? childStyleWidth
                : hasStyleDrivenWidth
                  ? childStyleWidth
                : (renderChild.size?.width !== undefined && renderChild.size?.width !== null
                  ? (typeof renderChild.size.width === 'number' ? `${renderChild.size.width}px` : renderChild.size.width)
                  : childStyleWidth)
              ,
              // Preserve base style width overrides for flex children when size is only carrying the
              // generic fill value. ComponentWrapper strips width-related style properties for flex
              // children, so the outer wrapper must own them.
              ...((renderChild.size?.width === '100%' || renderChild.size?.width === undefined || renderChild.size?.width === null) &&
                 childStyle?.width !== undefined &&
                 childStyle.width !== '100%' &&
                 childStyle.width !== 'auto'
                ? {
                    width: childStyleWidth,
                  }
                : {}),

              // Apply height from child.size, but skip percentage heights in column layouts when parent has auto height
              // Percentage heights don't work when parent has height: auto - they cause layout issues
              height: (() => {
                if (childHasContentDrivenHeight) {
                  return undefined;
                }
                if (shouldUseV2StyleHeight) {
                  return childStyleHeight;
                }
                if (renderChild.size?.height === undefined || renderChild.size?.height === null) {
                  return undefined;
                }
                const heightValue = renderChild.size.height;
                const isPercentageHeight = typeof heightValue === 'string' && heightValue.includes('%');
                const isColumnLayout = responsiveProperties.flexDirection === 'column' || responsiveProperties.flexDirection === 'column-reverse';
                const parentHasAutoHeight = !(narrowDimension(style?.['height'])) && !(narrowDimension(renderComponent.properties['height'])) && !(responsiveProperties.height);

                // Don't apply percentage heights in column layouts when parent has auto height
                if (isPercentageHeight && isColumnLayout && parentHasAutoHeight) {
                  return undefined;
                }

                return typeof heightValue === 'number' ? `${heightValue}px` : heightValue;
              })(),
              // Apply responsive size constraints (minWidth, maxWidth, etc.)
              ...(() => {
                const sizeStyles: React.CSSProperties = {};

                // Apply responsive breakpoints in both editing and viewer modes for consistent behavior
                if (renderChild.responsivePosition) {
                  const breakpoint = getBreakpointFromViewport(activeViewport);

                  // Check for breakpoint-specific styles that include width/height and min/max constraints
                  const breakpointStyle = renderChild.responsivePosition.breakpoints?.[breakpoint]?.style;
                  // Check if this breakpoint has flexBasis: 100% (full width child)
                  const breakpointConstraints = renderChild.responsivePosition.breakpoints?.[breakpoint]?.constraints;
                  const isFullWidthChild = String(breakpointConstraints?.flexBasis) === '100%';

                  // Check if parent has auto height (percentage heights don't work in this case)
                  const isColumnLayout = responsiveProperties.flexDirection === 'column' || responsiveProperties.flexDirection === 'column-reverse';
                  const parentHasAutoHeight = !(narrowDimension(style?.['height'])) && !(narrowDimension(renderComponent.properties['height'])) && !(responsiveProperties.height);

                  if (breakpointStyle) {
                    // Apply width/height from breakpoint style first (highest priority)
                    const width = breakpointStyle.width;
                    if (width && (typeof width === 'string' || typeof width === 'number')) {
                      sizeStyles.width = typeof width === 'number' ? `${width}px` : width;
                    }
                    const height = breakpointStyle.height;
                    // Skip percentage heights in column layouts with auto-height parent
                    const isPercentageHeight = typeof height === 'string' && height.includes('%');
                    if (height && (typeof height === 'string' || typeof height === 'number') &&
                        !childHasContentDrivenHeight &&
                        !(isPercentageHeight && isColumnLayout && parentHasAutoHeight)) {
                      sizeStyles.height = typeof height === 'number' ? `${height}px` : height;
                    }

                    // Apply CSS order property for flexbox layout control
                    const order = breakpointStyle.order;
                    if (order !== undefined && (typeof order === 'string' || typeof order === 'number')) {
                      sizeStyles.order = order;
                    }

                    // For full-width children (flexBasis: 100%), don't apply minWidth/maxWidth constraints
                    // These constraints are meant for multi-column layouts, not stacked mobile layouts
                    const minWidth = breakpointStyle.minWidth;
                    if (minWidth && (typeof minWidth === 'string' || typeof minWidth === 'number') && !isFullWidthChild) {
                      sizeStyles.minWidth = minWidth;
                    }
                    const maxWidth = breakpointStyle.maxWidth;
                    if (maxWidth && (typeof maxWidth === 'string' || typeof maxWidth === 'number') && !isFullWidthChild) {
                      sizeStyles.maxWidth = maxWidth;
                    }
                    const minHeight = breakpointStyle.minHeight;
                    if (minHeight && (typeof minHeight === 'string' || typeof minHeight === 'number')) {
                      sizeStyles.minHeight = minHeight;
                    }
                    const maxHeight = breakpointStyle.maxHeight;
                    if (maxHeight && (typeof maxHeight === 'string' || typeof maxHeight === 'number')) {
                      sizeStyles.maxHeight = maxHeight;
                    }
                    const display = breakpointStyle.display;
                    if (display !== undefined) {
                      const narrowed = narrowDisplay(display);
                      if (narrowed !== undefined) {
                        sizeStyles.display = narrowed;
                      }
                    }
                  }

                  // Check breakpoint-specific size first, then fall back to base responsiveSize
                  const breakpointResponsiveSize = renderChild.responsivePosition.breakpoints?.[breakpoint]?.size;
                  const baseResponsiveSize = renderChild.responsivePosition.responsiveSize;
                  const responsiveSize = breakpointResponsiveSize || baseResponsiveSize;

                  if (responsiveSize) {
                    // Apply width from responsiveSize only if not already set from breakpointStyle
                    if (!sizeStyles.width && responsiveSize.width) {
                      const width = responsiveSize.width;
                      if (typeof width === 'string' || typeof width === 'number') {
                        sizeStyles.width = width === '100%'
                          ? '100%'
                          : width;
                      }
                    }

                    // Apply height from responsiveSize only if not already set from breakpointStyle
                    // Skip percentage heights in column layouts with auto-height parent
                    if (!sizeStyles.height && responsiveSize.height && !childHasContentDrivenHeight) {
                      const height = responsiveSize.height;
                      const isRsPercentageHeight = typeof height === 'string' && height.includes('%');
                      if ((typeof height === 'string' || typeof height === 'number') &&
                          !(isRsPercentageHeight && isColumnLayout && parentHasAutoHeight)) {
                        sizeStyles.height = typeof height === 'number' ? `${height}px` : height;
                      }
                    }

                    // FILE_STORAGE components auto-grow on mobile — override any fixed height
                    if (renderChild.type === ComponentType.FILE_STORAGE && getBreakpointFromViewport(activeViewport) === 'mobile') {
                      sizeStyles.height = 'auto';
                    }

                    // Apply constraints if not already set from breakpointStyle
                    // Skip minWidth/maxWidth for full-width children (mobile stacked layout)
                    if (!breakpointStyle?.minWidth && !isFullWidthChild) {
                      const rsMinWidth = responsiveSize.minWidth;
                      if (rsMinWidth && (typeof rsMinWidth === 'string' || typeof rsMinWidth === 'number')) {
                        sizeStyles.minWidth = rsMinWidth;
                      }
                    }
                    if (!breakpointStyle?.maxWidth && !isFullWidthChild) {
                      const rsMaxWidth = responsiveSize.maxWidth;
                      if (rsMaxWidth && (typeof rsMaxWidth === 'string' || typeof rsMaxWidth === 'number')) {
                        sizeStyles.maxWidth = rsMaxWidth;
                      }
                    }
                    if (!sizeStyles.minHeight) {
                      const rsMinHeight = responsiveSize.minHeight;
                      if (rsMinHeight && (typeof rsMinHeight === 'string' || typeof rsMinHeight === 'number')) {
                        sizeStyles.minHeight = rsMinHeight;
                      }
                    }
                    if (!sizeStyles.maxHeight) {
                      const rsMaxHeight = responsiveSize.maxHeight;
                      if (rsMaxHeight && (typeof rsMaxHeight === 'string' || typeof rsMaxHeight === 'number')) {
                        sizeStyles.maxHeight = rsMaxHeight;
                      }
                    }
                  }
                }

                // FIX: For chart and table components WITHOUT responsivePosition, default to 100% width
                // This ensures auto-container charts span full width in viewer mode
                if (!renderChild.responsivePosition && interactionContext.mode !== 'editing') {
                  const isChartComponent = renderChild.type === ComponentType.LINE_CHART ||
                    renderChild.type === ComponentType.BAR_CHART ||
                    renderChild.type === ComponentType.PIE_CHART ||
                    renderChild.type === ComponentType.SCATTER_CHART ||
                    renderChild.type === ComponentType.AREA_CHART ||
                    renderChild.type === ComponentType.HEATMAP_CHART ||
                    renderChild.type === ComponentType.TREEMAP_CHART ||
                    renderChild.type === ComponentType.GAUGE_CHART ||
                    renderChild.type === ComponentType.MAP_CHART;

                  const isTableComponent = renderChild.type === ComponentType.TABLE ||
                    renderChild.type === ComponentType.ADVANCED_TABLE;

                  if ((isChartComponent || isTableComponent) && !sizeStyles.width) {
                    sizeStyles.width = '100%';
                  }
                }

                return sizeStyles;
              })(),
              // Check component properties for min/max constraints as fallback
	              ...(() => {
	                const propConstraints: React.CSSProperties = {};

                // Check if constraints are already set from responsive/breakpoint styles
                if (renderChild.responsivePosition) {
                  const breakpoint = getBreakpointFromViewport(activeViewport);
                  const breakpointStyle = renderChild.responsivePosition?.breakpoints?.[breakpoint]?.style;
                  const breakpointResponsiveSize = renderChild.responsivePosition?.breakpoints?.[breakpoint]?.size;
                  const baseResponsiveSize = renderChild.responsivePosition?.responsiveSize;
                  const responsiveSize = breakpointResponsiveSize || baseResponsiveSize;

                  // Check if this breakpoint has flexBasis: 100% (full width child)
                  const breakpointConstraints = renderChild.responsivePosition.breakpoints?.[breakpoint]?.constraints;
                  const isFullWidthChild = String(breakpointConstraints?.flexBasis) === '100%';

                  // Only apply property constraints if not already set from responsive/breakpoint styles
                  // Skip minWidth/maxWidth for full-width children (mobile stacked layout)
	                  const hasBreakpointMinWidth = breakpointStyle?.minWidth !== undefined;
	                  const hasResponsiveMinWidth = responsiveSize?.minWidth !== undefined;
	                  const childStyleMinWidth = formatDimensionForStyle(childStyle?.minWidth);
	                  const propertyMinWidth = formatDimensionForStyle(renderChild.properties?.minWidth);
	                  if (!hasBreakpointMinWidth && !hasResponsiveMinWidth && !isFullWidthChild && childStyleMinWidth !== undefined) {
	                    propConstraints.minWidth = childStyleMinWidth;
	                  } else if (!hasBreakpointMinWidth && !hasResponsiveMinWidth && !isFullWidthChild && propertyMinWidth !== undefined) {
	                    propConstraints.minWidth = propertyMinWidth;
	                  }

	                  const hasBreakpointMaxWidth = breakpointStyle?.maxWidth !== undefined;
	                  const hasResponsiveMaxWidth = responsiveSize?.maxWidth !== undefined;
	                  const childStyleMaxWidth = formatDimensionForStyle(childStyle?.maxWidth);
	                  const propertyMaxWidth = formatDimensionForStyle(renderChild.properties?.maxWidth);
	                  if (!hasBreakpointMaxWidth && !hasResponsiveMaxWidth && !isFullWidthChild && childStyleMaxWidth !== undefined) {
	                    propConstraints.maxWidth = childStyleMaxWidth;
	                  } else if (!hasBreakpointMaxWidth && !hasResponsiveMaxWidth && !isFullWidthChild && propertyMaxWidth !== undefined) {
	                    propConstraints.maxWidth = propertyMaxWidth;
	                  }

	                  const hasBreakpointMinHeight = breakpointStyle?.minHeight !== undefined;
	                  const hasResponsiveMinHeight = responsiveSize?.minHeight !== undefined;
	                  const propertyMinHeight = formatDimensionForStyle(renderChild.properties?.minHeight);
	                  if (!hasBreakpointMinHeight && !hasResponsiveMinHeight && propertyMinHeight !== undefined) {
	                    propConstraints.minHeight = propertyMinHeight;
	                  }

	                  const hasBreakpointMaxHeight = breakpointStyle?.maxHeight !== undefined;
	                  const hasResponsiveMaxHeight = responsiveSize?.maxHeight !== undefined;
	                  const propertyMaxHeight = formatDimensionForStyle(renderChild.properties?.maxHeight);
	                  if (!hasBreakpointMaxHeight && !hasResponsiveMaxHeight && propertyMaxHeight !== undefined && !hasLegacyDefaultTableMaxHeight(renderChild)) {
	                    propConstraints.maxHeight = propertyMaxHeight;
	                  }
	                } else {
	                  // When no responsive position, apply properties directly
	                  const childStyleMinWidth = formatDimensionForStyle(childStyle?.minWidth);
	                  const propertyMinWidth = formatDimensionForStyle(renderChild.properties?.minWidth);
	                  if (childStyleMinWidth !== undefined) {
	                    propConstraints.minWidth = childStyleMinWidth;
	                  } else if (propertyMinWidth !== undefined) {
	                    propConstraints.minWidth = propertyMinWidth;
	                  }
	                  const childStyleMaxWidth = formatDimensionForStyle(childStyle?.maxWidth);
	                  const propertyMaxWidth = formatDimensionForStyle(renderChild.properties?.maxWidth);
	                  if (childStyleMaxWidth !== undefined) {
	                    propConstraints.maxWidth = childStyleMaxWidth;
	                  } else if (propertyMaxWidth !== undefined) {
	                    propConstraints.maxWidth = propertyMaxWidth;
	                  }
	                  const propertyMinHeight = formatDimensionForStyle(renderChild.properties?.minHeight);
	                  if (propertyMinHeight !== undefined) {
	                    propConstraints.minHeight = propertyMinHeight;
	                  }
	                  const propertyMaxHeight = formatDimensionForStyle(renderChild.properties?.maxHeight);
	                  if (propertyMaxHeight !== undefined && !hasLegacyDefaultTableMaxHeight(renderChild)) {
	                    propConstraints.maxHeight = propertyMaxHeight;
	                  }
	                }

                return propConstraints;
              })(),
              alignSelf: (() => {
                // Check breakpoint-specific constraints first
                // Apply in ALL modes (editing and viewer) for consistent WYSIWYG rendering
                if (renderChild.responsivePosition?.breakpoints) {
                  const breakpoint = getBreakpointFromViewport(activeViewport);
                  const breakpointConstraints = renderChild.responsivePosition.breakpoints[breakpoint]?.constraints;
                  if (breakpointConstraints?.alignSelf) {
                    return breakpointConstraints.alignSelf;
                  }
                }
                // Fall back to base constraints, then properties, then default
                return normalizedChildConstraints.alignSelf as React.CSSProperties['alignSelf'] ||
                       (renderChild.properties?.alignSelf as React.CSSProperties['alignSelf']) ||
                       "auto";
              })(),
              // Apply scroll snap align for carousel items
              scrollSnapAlign: (renderChild.properties?.scrollSnapAlign as React.CSSProperties['scrollSnapAlign']),
              // Ensure child takes full height in row layout ONLY if no explicit height is set
              // BUT: Don't apply height: 100% to charts/tables - they need to grow based on minimums
              // AND: Don't apply height: 100% when alignSelf: stretch is set - let flex handle it
              ...(() => {
                const isRowLayout = responsiveProperties.flexDirection === 'row' || responsiveProperties.flexDirection === 'row-reverse';
                const hasExplicitHeight = renderChild.size?.height !== undefined && renderChild.size?.height !== null;

                // Check if this is a chart or table component
                const isChartOrTable = renderChild.type === ComponentType.CHART ||
                  renderChild.type === ComponentType.BAR_CHART ||
                  renderChild.type === ComponentType.LINE_CHART ||
                  renderChild.type === ComponentType.PIE_CHART ||
                  renderChild.type === ComponentType.SCATTER_CHART ||
                  renderChild.type === ComponentType.AREA_CHART ||
                  renderChild.type === ComponentType.HEATMAP_CHART ||
                  renderChild.type === ComponentType.TREEMAP_CHART ||
                  renderChild.type === ComponentType.GAUGE_CHART ||
                  renderChild.type === ComponentType.MAP_CHART ||
                  renderChild.type === ComponentType.TABLE ||
                  renderChild.type === ComponentType.ADVANCED_TABLE;

                // Check if alignSelf: stretch is set (in constraints or breakpoint constraints)
                // Apply in ALL modes (editing and viewer) for consistent WYSIWYG rendering
                const hasAlignSelfStretch = (() => {
                  // Check breakpoint constraints first - apply in ALL modes
                  if (renderChild.responsivePosition?.breakpoints) {
                    const breakpoint = getBreakpointFromViewport(activeViewport);
                    const bpConstraints = renderChild.responsivePosition.breakpoints[breakpoint]?.constraints;
                    if (bpConstraints?.alignSelf === 'stretch') return true;
                  }
                  // Check base constraints
                  if (normalizedChildConstraints.alignSelf === 'stretch') return true;
                  // Check properties
                  if (renderChild.properties?.alignSelf === 'stretch') return true;
                  return false;
                })();

                // Don't constrain chart/table height - let them grow based on minimums
                // Don't apply height: 100% when alignSelf: stretch - let flex container handle cross-axis sizing
                return isRowLayout && !hasExplicitHeight && !isChartOrTable && !hasAlignSelfStretch ? { height: '100%' } : undefined;
              })(),
              // Add margin and boxShadow from child properties or responsive styles
              // Apply in ALL modes (editing and viewer) for consistent WYSIWYG rendering
              ...(() => {
                const styles: React.CSSProperties = {};

                // For button components, skip border/borderRadius/boxShadow on wrapper
                // Buttons handle these properties internally to prevent double borders
                const isButton = renderChild.type === ComponentType.BUTTON;

                // Check for margin, boxShadow, border, and borderRadius in responsive breakpoints first
                // Apply in ALL modes for consistent rendering
                if (renderChild.responsivePosition) {
                  const breakpoint = getBreakpointFromViewport(activeViewport);
                  const breakpointStyle = renderChild.responsivePosition.breakpoints?.[breakpoint]?.style;

                  if (breakpointStyle?.margin !== undefined) {
                    const bm = breakpointStyle.margin;
                    styles.margin = typeof bm === 'number' ? `${bm}px` : (typeof bm === 'string' ? bm : undefined);
                  }
                  if (!isButton && breakpointStyle?.boxShadow !== undefined) {
                    const v = breakpointStyle.boxShadow;
                    if (typeof v === 'string') styles.boxShadow = v;
                  }
                  if (!isButton && breakpointStyle?.border !== undefined) {
                    const v = breakpointStyle.border;
                    if (typeof v === 'string') styles.border = v;
                  }
                  if (!isButton && breakpointStyle?.borderRadius !== undefined) {
                    const r = breakpointStyle.borderRadius;
                    styles.borderRadius = typeof r === 'number' ? `${r}px` : (typeof r === 'string' ? r : undefined);
                  }
                }

                // If not found in breakpoints, check main properties
                if (styles.margin === undefined && renderChild.properties?.margin !== undefined) {
                  const pm = renderChild.properties.margin;
                  styles.margin = typeof pm === 'number' ? `${pm}px` : (typeof pm === 'string' ? pm : undefined);
                }
                if (!isButton && styles.boxShadow === undefined && renderChild.properties?.boxShadow !== undefined) {
                  const v = renderChild.properties.boxShadow;
                  if (typeof v === 'string') styles.boxShadow = v;
                }
                if (!isButton && styles.border === undefined && renderChild.properties?.border !== undefined) {
                  const v = renderChild.properties.border;
                  if (typeof v === 'string') styles.border = v;
                }
                if (!isButton && styles.borderRadius === undefined && renderChild.properties?.borderRadius !== undefined) {
                  const r = renderChild.properties.borderRadius;
                  styles.borderRadius = typeof r === 'number' ? `${r}px` : (typeof r === 'string' ? r : undefined);
                }

                // Add overflow hidden when we have borderRadius to ensure proper clipping
                // But not for buttons - they handle this internally
                if (!isButton && styles.borderRadius) {
                  styles.overflow = 'hidden';
                }

                // Apply positioning properties from child.properties (for absolute positioning in flex)
                if (renderChild.properties?.position !== undefined) {
                  const pos = renderChild.properties.position;
                  if (typeof pos === 'string') {
                    styles.position = pos as React.CSSProperties['position'];
                  }

                  // Apply position coordinates if specified
                  if (renderChild.properties.top !== undefined) {
                    const t = renderChild.properties.top;
                    styles.top = typeof t === 'number' ? `${t}px` : (typeof t === 'string' ? t : undefined);
                  }
                  if (renderChild.properties.right !== undefined) {
                    const r = renderChild.properties.right;
                    styles.right = typeof r === 'number' ? `${r}px` : (typeof r === 'string' ? r : undefined);
                  }
                  if (renderChild.properties.bottom !== undefined) {
                    const b = renderChild.properties.bottom;
                    styles.bottom = typeof b === 'number' ? `${b}px` : (typeof b === 'string' ? b : undefined);
                  }
                  if (renderChild.properties.left !== undefined) {
                    const l = renderChild.properties.left;
                    styles.left = typeof l === 'number' ? `${l}px` : (typeof l === 'string' ? l : undefined);
                  }
                }

                return styles;
              })(),
            };

            const isChartComponent = renderChild.type === ComponentType.CHART ||
              renderChild.type === ComponentType.BAR_CHART ||
              renderChild.type === ComponentType.LINE_CHART ||
              renderChild.type === ComponentType.PIE_CHART ||
              renderChild.type === ComponentType.SCATTER_CHART ||
              renderChild.type === ComponentType.AREA_CHART ||
              renderChild.type === ComponentType.HEATMAP_CHART ||
              renderChild.type === ComponentType.TREEMAP_CHART ||
              renderChild.type === ComponentType.GAUGE_CHART ||
              renderChild.type === ComponentType.MAP_CHART;

            const isTableComponent = renderChild.type === ComponentType.TABLE ||
              renderChild.type === ComponentType.ADVANCED_TABLE;
            const isContentDrivenTable = isTableComponent &&
              (renderChild.properties?.maxHeight === undefined || hasLegacyDefaultTableMaxHeight(renderChild)) &&
              renderChild.properties?.enablePagination !== false;

            // Check if Step 3 Layout Optimization has set flex constraints
            // Don't override if Step 3 has already optimized the layout
            const hasStep3Constraints =
              normalizedChildConstraints.flexGrow !== undefined ||
              normalizedChildConstraints.flexShrink !== undefined ||
              normalizedChildConstraints.flexBasis !== undefined;

            // Only override flex for charts/tables that don't have explicit sizing
            // Skip override if component has explicit flex constraints or explicit width/height
            // CRITICAL: Also skip if Step 3 Layout Optimization has set constraints
            const hasExplicitFlexBasis = childStyles.flexBasis !== undefined && childStyles.flexBasis !== 'auto';

            // PHASE 1 BUG FIX: Check for user-set width
            // IMPORTANT: Do NOT use size.width for charts/tables - it always has a default (400)
            // which makes it unreliable for detecting user intent.
            // Only use explicit style overrides that indicate user action:
            // 1. responsivePosition.breakpoints[bp].style.width - Phase 5.4 merger writes here
            // 2. child.style?.width that is NOT '100%' - explicit width set by user
            const breakpointStyleWidth = renderChild.responsivePosition?.breakpoints?.[breakpoint]?.style?.width;
            const hasBreakpointWidth = breakpointStyleWidth !== undefined && breakpointStyleWidth !== null && breakpointStyleWidth !== '100%';

            // Check child.style.width for explicit non-100% values (100% means "fill container")
            const directStyleWidth = (renderChild.style as React.CSSProperties)?.width;
            const hasDirectStyleWidth = directStyleWidth !== undefined && directStyleWidth !== null &&
              directStyleWidth !== '100%' && directStyleWidth !== 'auto';

            // Determine the explicit sizing value (priority: breakpoint > direct style)
            let explicitSizingValue: string | number | null = null;
            if (hasBreakpointWidth && (typeof breakpointStyleWidth === 'string' || typeof breakpointStyleWidth === 'number')) {
              explicitSizingValue = typeof breakpointStyleWidth === 'number' ? `${breakpointStyleWidth}px` : breakpointStyleWidth;
            } else if (hasDirectStyleWidth && (typeof directStyleWidth === 'string' || typeof directStyleWidth === 'number')) {
              explicitSizingValue = typeof directStyleWidth === 'number' ? `${directStyleWidth}px` : directStyleWidth;
            }

            // PHASE 1 BUG FIX: Handle explicit width properly for charts/tables
            // Previously, charts/tables with explicit width still got flexBasis: 100% override
            // which caused the wrapper to stay full-width (phantom border bug)
            //
            // PHASE 5 FIX: Be direction-aware when converting width to flexBasis
            // - In ROW direction: flexBasis controls width (main axis), so width → flexBasis is correct
            // - In COLUMN direction: flexBasis controls height (main axis), so width must stay as width
            const isRowDirection = responsiveProperties.flexDirection === 'row' || responsiveProperties.flexDirection === 'row-reverse';

            if ((childStyles.width === '100%' || isChartComponent || isTableComponent) &&
                !hasExplicitFlexBasis && !hasStep3Constraints) {
              if (explicitSizingValue) {
                if (isRowDirection) {
                  // ROW direction: width → flexBasis for proper flex behavior
                  childStyles.flexGrow = 0;
                  childStyles.flexShrink = 0;
                  childStyles.flexBasis = explicitSizingValue as string;
                  childStyles.width = undefined;
                } else {
                  // COLUMN direction: keep width as width, don't convert to flexBasis
                  // flexBasis in column controls height, not width
                  childStyles.flexGrow = 0;
                  childStyles.flexShrink = 0;
                  childStyles.flexBasis = 'auto'; // Let height determine main axis size
                  childStyles.width = explicitSizingValue as string;
                }
              } else {
                // No explicit width - default to full width behavior for charts/tables
                childStyles.flexGrow = isContentDrivenTable ? 0 : 1;
                childStyles.flexShrink = isContentDrivenTable ? 0 : 1;
                childStyles.flexBasis = isRowDirection ? '100%' : 'auto';
                if (!isRowDirection) {
                  childStyles.width = '100%'; // Full width in column direction
                }
              }
            }

            // Use childStyles directly
            const mergedStyles = childStyles;

            return (
              <div
                key={child.id}
                className={cn(
                  "relative group transition-all duration-200",
                  isRefreshBadge ? "pointer-events-none" : "pointer-events-auto",
                  // Don't apply flex-shrink-0 if we have responsive flex constraints
                  (() => {
                    // Check if we have flex constraints in responsive position
                    // Apply in ALL modes (including editing) for proper mobile preview
                    if (renderChild.responsivePosition) {
                      const breakpoint = getBreakpointFromViewport(activeViewport);
                      const hasResponsiveConstraints =
                        Object.keys(normalizedChildConstraints).length > 0 ||
                        renderChild.responsivePosition.breakpoints?.[breakpoint]?.constraints ||
                        renderChild.responsivePosition.breakpoints?.[breakpoint]?.style?.flex;

                      if (hasResponsiveConstraints) {
                        return ""; // Don't add flex-shrink-0
                      }
                    }
                    // Only apply flex-shrink-0 if no flex property at all
                    return !renderChild.properties?.flex ? "flex-shrink-0" : "";
                  })(),
                  !isRefreshBadge && isSelected && "ring-2 ring-primary ring-offset-2 ring-offset-background",
                  !isRefreshBadge && isFocused && "outline-2 outline-primary outline-offset-2",
                  !isRefreshBadge && !isPreview && "hover:ring-1 hover:ring-primary/50"
                )}
                data-child-id={child.id}
                data-child-index={index}
                data-selected={isSelected}
                data-focused={isFocused}
                tabIndex={isRefreshBadge ? -1 : (!isPreview ? 0 : -1)}
                role={isRefreshBadge ? "presentation" : "button"}
                aria-label={isRefreshBadge ? undefined : `Component`}
                style={mergedStyles}
                // Click handling is now done by SelectableWrapper
                onContextMenu={!isPreview && !isRefreshBadge ? (e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  setContextMenu({
                    show: true,
                    x: e.clientX,
                    y: e.clientY,
                    childId: child.id,
                    isMultiSelect: selectionState.selectedChildren.length > 1
                  });
                } : undefined}
                onKeyDown={!isPreview && !isRefreshBadge ? (e) => {
                  // Handle delete key for child removal
                  if (e.key === 'Delete' || e.key === 'Backspace') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (selectionState.selectedChildren.length > 1) {
                      removeChildrenWithConfirmation(selectionState.selectedChildren);
                    } else {
                      removeChildWithConfirmation(child.id);
                    }
                    return;
                  }

                  // Handle duplicate shortcut (Cmd+D / Ctrl+D)
                  if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
                    e.preventDefault();
                    e.stopPropagation();
                    if (selectionState.selectedChildren.length > 1) {
                      duplicateChildren(selectionState.selectedChildren);
                    } else {
                      duplicateChild(child.id);
                    }
                    return;
                  }

                  // Pass through to default handler
                  childKeydownHandler(e);
                } : undefined}
              >
                {/* Selection indicator */}
                {isSelected && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full border-2 border-background flex items-center justify-center z-20">
                    <svg width="8" height="8" viewBox="0 0 8 8" className="fill-background">
                      <path d="M2.5 4L3.5 5L5.5 3" stroke="currentColor" strokeWidth="1" fill="none" />
                    </svg>
                  </div>
                )}


                <ComponentWrapper
                  component={renderChild}
                  isFlexChild={true}
                  flexDirection={responsiveProperties.flexDirection}
                  viewport={activeViewport}
                  activeColorScheme={activeColorScheme}
                />
              </div>
            );
          })}



          {/* Enhanced visual indicator when container has children */}
          {/* {!isPreview && (
            <div className={cn(
              "absolute -top-7 left-0 px-3 py-1.5 text-xs rounded-md font-medium pointer-events-none transition-all duration-300 shadow-lg border backdrop-blur-sm animate-in fade-in-50 slide-in-from-top-2",
              hasChildSelection() ?
                "bg-blue-100/90 text-blue-800 border-blue-300 shadow-blue-200/50" :
                "bg-white/90 text-slate-700 border-slate-300 shadow-slate-200/50"
            )}>
              <div className="flex items-center gap-2">
                <span className="text-sm">
                  {responsiveProperties.flexDirection === 'row' || responsiveProperties.flexDirection === 'row-reverse' ? '📏' : '📚'}
                </span>
                <span>
                  {responsiveProperties.label || "Flex Container"} ({children.length} item{children.length !== 1 ? "s" : ""})
                </span>
                {hasChildSelection() && (
                  <span className="bg-blue-200 text-blue-800 px-1.5 py-0.5 rounded text-xs font-semibold animate-in zoom-in-95 duration-200">
                    ✨ {selectionState.selectedChildren.length} selected
                  </span>
                )}
                {enableSpatialTracking && spatialSystem && (
                  <span className="text-xs opacity-70 font-mono bg-slate-200/50 px-1.5 py-0.5 rounded">
                    📊 {Math.round(flexContainerSpatial.getSpatialMetrics().areaUtilization * 100)}%
                  </span>
                )}
              </div>
            </div>
          )} */}
        </div>
      )}

      {/* Enhanced container resize handles - Disabled for cleaner UI */}
      {/* {!isPreview && !isEmpty && (
        <div className="absolute inset-0 pointer-events-none group">
          <div className={cn(
            "absolute -bottom-1.5 -right-1.5 w-4 h-4 rounded-sm pointer-events-auto transition-all duration-300 border-2 border-white shadow-lg",
            hasChildSelection() ?
              "bg-blue-500 hover:bg-blue-600 cursor-se-resize hover:scale-110" :
              "bg-slate-400 hover:bg-slate-500 cursor-se-resize opacity-60 hover:opacity-100"
          )}>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-1 h-1 bg-white rounded-full" />
            </div>
          </div>

          <div className="absolute inset-0 opacity-0 group-hover:opacity-40 transition-opacity duration-300 pointer-events-none">
            <div className="absolute top-0 left-2 right-2 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent" />
            <div className="absolute bottom-0 left-2 right-2 h-px bg-gradient-to-r from-transparent via-blue-400 to-transparent" />
            <div className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-blue-400 to-transparent" />
            <div className="absolute right-0 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-blue-400 to-transparent" />
          </div>
        </div>
      )} */}

      {/* Visual feedback for container interactions */}
      {!isPreview && hasChildSelection() && (
        <div className="absolute -inset-1 rounded-lg pointer-events-none animate-in fade-in-50 duration-300">
          <div className="absolute inset-0 rounded-lg border-2 border-blue-400/60 shadow-lg shadow-blue-400/20 animate-pulse" />
          <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-blue-400/10 via-purple-400/5 to-blue-400/10" />
        </div>
      )}

      {/* Spatial debugging removed - flex layout handles all positioning */}


      {/* Child management confirmation dialog */}
      <AlertDialog open={confirmationState.isOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmationState.title}</AlertDialogTitle>
            <AlertDialogDescription className="whitespace-pre-line">
              {confirmationState.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelOperation}>
              {confirmationState.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmOperation}
              className={cn(
                confirmationState.variant === 'destructive' &&
                "bg-destructive text-destructive-foreground hover:bg-destructive/90"
              )}
            >
              {confirmationState.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Child context menu */}
      {contextMenu.show && !isPreview && (
        <>
          {/* Backdrop to close menu */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setContextMenu(prev => ({ ...prev, show: false }))}
          />

          {/* Context menu */}
          <div
            className="fixed z-50 min-w-[160px] bg-background border border-border rounded-md shadow-lg py-1"
            style={{
              left: `${contextMenu.x}px`,
              top: `${contextMenu.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {contextMenu.isMultiSelect ? (
              // Multi-select menu
              <>
                <button
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                  onClick={() => {
                    duplicateChildren(selectionState.selectedChildren);
                    setContextMenu(prev => ({ ...prev, show: false }));
                  }}
                >
                  📄 Duplicate {selectionState.selectedChildren.length} items
                </button>
                <hr className="my-1 border-border" />
                <button
                  className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  onClick={() => {
                    removeChildrenWithConfirmation(selectionState.selectedChildren);
                    setContextMenu(prev => ({ ...prev, show: false }));
                  }}
                >
                  🗑️ Remove {selectionState.selectedChildren.length} items
                </button>
              </>
            ) : (
              // Single-select menu
              <>
                <button
                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
                  onClick={() => {
                    if (contextMenu.childId) {
                      duplicateChild(contextMenu.childId);
                    }
                    setContextMenu(prev => ({ ...prev, show: false }));
                  }}
                >
                  📄 Duplicate
                </button>
                <hr className="my-1 border-border" />
                <button
                  className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors"
                  onClick={() => {
                    if (contextMenu.childId) {
                      removeChildWithConfirmation(contextMenu.childId);
                    }
                    setContextMenu(prev => ({ ...prev, show: false }));
                  }}
                >
                  🗑️ Remove
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// Enhanced interface for preset configurations
export interface FlexContainerPreset extends FlexContainerProperties {
  description: string;
  icon: string;
  category: 'basic' | 'alignment' | 'styled' | 'navigation' | 'marketing' | 'controls' | 'layout' | 'forms';
}

// Utility function to create default flex container properties
const getDefaultFlexContainerProperties = (
  direction: "row" | "column" = "column",
): FlexContainerProperties => {
  return {
    flexDirection: direction,
    justifyContent: "flex-start",
    alignItems: "stretch",
    flexWrap: "nowrap",
    gap: 16,
    padding: 16,
    backgroundColor: "transparent",
    borderRadius: 8,
    minHeight: direction === "column" ? 200 : 80,
    label: direction === "column" ? "Vertical Stack" : "Horizontal Row",
  };
};

// Preset categories for organization
const _PresetCategories = {
  basic: { label: 'Basic Layouts', icon: '🔧', color: 'blue' },
  alignment: { label: 'Alignment', icon: '📐', color: 'green' },
  styled: { label: 'Styled Containers', icon: '🎨', color: 'purple' },
  navigation: { label: 'Navigation', icon: '🧭', color: 'indigo' },
  marketing: { label: 'Marketing', icon: '🌟', color: 'yellow' },
  controls: { label: 'Controls', icon: '🔘', color: 'gray' },
  layout: { label: 'Advanced Layout', icon: '⚡', color: 'orange' },
  forms: { label: 'Forms', icon: '📝', color: 'red' },
} as const;

// Preset configurations for common layout patterns
const _FlexContainerPresets = {
  verticalStack: {
    ...getDefaultFlexContainerProperties("column"),
    label: "Vertical Stack",
    justifyContent: "flex-start",
    alignItems: "stretch",
    gap: 12,
    padding: 16,
    minHeight: 200,
    description: "Stack components vertically with consistent spacing",
    icon: "📚",
    category: "basic",
  },

  horizontalRow: {
    ...getDefaultFlexContainerProperties("row"),
    label: "Horizontal Row",
    justifyContent: "flex-start",
    alignItems: "center",
    gap: 16,
    padding: 16,
    minHeight: 80,
    description: "Arrange components horizontally in a row",
    icon: "📏",
    category: "basic",
  },

  centeredColumn: {
    ...getDefaultFlexContainerProperties("column"),
    label: "Centered Column",
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
    padding: 24,
    minHeight: 250,
    description: "Center all content both vertically and horizontally",
    icon: "🎯",
    category: "alignment",
  },

  spaceBetween: {
    ...getDefaultFlexContainerProperties("row"),
    label: "Space Between",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 0,
    padding: 16,
    minHeight: 80,
    description: "Distribute items with equal space between them",
    icon: "↔️",
    category: "alignment",
  },

  cardLayout: {
    ...getDefaultFlexContainerProperties("column"),
    label: "Card Layout",
    justifyContent: "flex-start",
    alignItems: "stretch",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 24,
    gap: 16,
    minHeight: 200,
    description: "Styled card container with border and background",
    icon: "🃏",
    category: "styled",
  },

  navbar: {
    ...getDefaultFlexContainerProperties("row"),
    label: "Navigation Bar",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    padding: 12,
    gap: 16,
    minHeight: 60,
    description: "Horizontal navigation with space between items",
    icon: "🧭",
    category: "navigation",
  },

  sidebar: {
    ...getDefaultFlexContainerProperties("column"),
    label: "Sidebar Layout",
    justifyContent: "flex-start",
    alignItems: "stretch",
    backgroundColor: "#f1f5f9",
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: 20,
    gap: 12,
    minHeight: 300,
    description: "Vertical sidebar with consistent navigation items",
    icon: "📋",
    category: "navigation",
  },

  heroSection: {
    ...getDefaultFlexContainerProperties("column"),
    label: "Hero Section",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f8fafc",
    borderRadius: 16,
    padding: 48,
    gap: 24,
    minHeight: 400,
    description: "Large centered hero section for landing pages",
    icon: "🌟",
    category: "marketing",
  },

  buttonGroup: {
    ...getDefaultFlexContainerProperties("row"),
    label: "Button Group",
    justifyContent: "flex-start",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
    padding: 12,
    minHeight: 60,
    description: "Group of buttons with consistent spacing",
    icon: "🔘",
    category: "controls",
  },

  gridAlternative: {
    ...getDefaultFlexContainerProperties("row"),
    label: "Flex Grid",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: 20,
    padding: 20,
    minHeight: 200,
    description: "Flexible grid-like layout using flex wrap",
    icon: "⚡",
    category: "layout",
  },

  formSection: {
    ...getDefaultFlexContainerProperties("column"),
    label: "Form Section",
    justifyContent: "flex-start",
    alignItems: "stretch",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 24,
    gap: 18,
    minHeight: 250,
    description: "Form container with proper spacing for inputs",
    icon: "📝",
    category: "forms",
  },
};
